// XLCheck - Sprint 44 XL.
// Checkbox primitivo global con tokens + dark mode + label/hint/error.
// Reemplaza el Check local de src/modules/clientes/form/components/FormFields.jsx.
// Usa un <input type="checkbox"> nativo + una box visual estilizada para
// permitir animacion del check y focus ring tokenizado sin perder a11y.
//
// Uso:
//   <XLCheck checked={v} onChange={setV} label="Aplicar impuesto" />
//   <XLCheck checked={v} onChange={setV} label="Autorretenedor"
//            hint="Marca si el cliente se autoretiene" />
import { forwardRef, useEffect, useId } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';

const CSS_ID = 'xl-check-css';
const CSS = `
.xl-check {
  display: inline-flex;
  align-items: flex-start;
  gap: 10px;
  font-family: var(--xl-font-sans);
  color: var(--xl-color-text);
  cursor: pointer;
  user-select: none;
}
.xl-check__input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0; height: 0;
}
.xl-check__box {
  width: 18px; height: 18px;
  border: 1px solid var(--xl-color-border-strong);
  border-radius: var(--xl-radius-sm);
  background: var(--xl-color-bg);
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms cubic-bezier(0,0,0.2,1),
              border-color 120ms cubic-bezier(0,0,0.2,1),
              box-shadow 120ms cubic-bezier(0,0,0.2,1);
  margin-top: 1px;
}
.xl-check__input:focus-visible + .xl-check__box {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: 2px;
}
.xl-check__input:checked + .xl-check__box {
  background: var(--xl-color-primary);
  border-color: var(--xl-color-primary);
}
.xl-check__input:disabled + .xl-check__box {
  background: var(--xl-color-surface-alt);
  border-color: var(--xl-color-border);
  opacity: 0.6;
}
.xl-check__tick {
  width: 10px; height: 10px;
  color: var(--xl-color-text-inverse);
  opacity: 0;
  transition: opacity 100ms cubic-bezier(0,0,0.2,1);
}
.xl-check__input:checked + .xl-check__box .xl-check__tick { opacity: 1 }

.xl-check--error .xl-check__box { border-color: var(--xl-color-danger) }
.xl-check__text {
  display: flex; flex-direction: column; gap: 2px;
  line-height: 1.35;
  font-size: 13px;
}
.xl-check__label { color: var(--xl-color-text) }
.xl-check__hint  { font-size: 11px; color: var(--xl-color-text-muted) }
.xl-check__err   { font-size: 11px; color: var(--xl-color-danger) }
`;

const XLCheck = forwardRef(function XLCheck({
  checked,
  onChange,
  label,
  hint,
  error,
  disabled = false,
  id,
  className = '',
  ...rest
}, ref) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const autoId = useId();
  const fieldId = id || ('xl-chk-' + autoId);

  const handle = (e) => {
    if (typeof onChange === 'function') onChange(e.target.checked);
    if (rest.onChange) rest.onChange(e);
  };

  const cls = [
    'xl-check',
    error ? 'xl-check--error' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <label className={cls} htmlFor={fieldId}>
      <input
        {...rest}
        id={fieldId}
        ref={ref}
        type="checkbox"
        className="xl-check__input"
        checked={!!checked}
        onChange={handle}
        disabled={disabled}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={
          error ? fieldId + '-err' : hint ? fieldId + '-hint' : undefined
        }
      />
      <span className="xl-check__box" aria-hidden="true">
        <svg
          className="xl-check__tick"
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="1.5,5.5 4,8 8.5,2.5" />
        </svg>
      </span>
      <span className="xl-check__text">
        {label && <span className="xl-check__label">{label}</span>}
        {!error && hint && (
          <span id={fieldId + '-hint'} className="xl-check__hint">{hint}</span>
        )}
        {error && (
          <span id={fieldId + '-err'} className="xl-check__err" role="alert">
            {error}
          </span>
        )}
      </span>
    </label>
  );
});

export default XLCheck;
